import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatINR, timeAgo } from "@/lib/format";
import { roomImageForSeed } from "@/lib/stockImages";
import SectionCard, { ViewAllLink } from "./SectionCard";

// Same stage keys as the kanban in EnquiriesPage; LOST is left out of the summary.
const STAGES = [
  { key: "NEW", label: "New" },
  { key: "QUALIFIED", label: "Qualified" },
  { key: "DESIGNING", label: "Design" },
  { key: "QUOTATION", label: "Quote" },
  { key: "WON", label: "Won" },
];

const TAG = {
  NEW: { label: "New", className: "bg-chart-3 text-white" },
  QUALIFIED: { label: "Follow-up", className: "bg-warning text-white" },
  DESIGNING: { label: "Designing", className: "bg-primary text-primary-foreground" },
  QUOTATION: { label: "Quotation", className: "bg-accent text-accent-foreground" },
};

export default function EnquiryPipeline({ enquiries }) {
  const counts = Object.fromEntries(STAGES.map((s) => [s.key, 0]));
  enquiries.forEach((e) => {
    if (counts[e.status] != null) counts[e.status] += 1;
  });

  const open = enquiries
    .filter((e) => TAG[e.status])
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    .slice(0, 3);

  return (
    <SectionCard testId="enquiry-pipeline" title="Enquiry Pipeline" action={<ViewAllLink to="/enquiries" />}>
      <div className="grid grid-cols-5 gap-1.5 mb-5">
        {STAGES.map((s) => (
          <Link
            key={s.key}
            to="/enquiries"
            data-testid={`pipeline-stage-${s.key}`}
            className={cn(
              "rounded-lg px-1 py-2 text-center transition-colors",
              s.key === "WON" ? "bg-success/10 hover:bg-success/15" : "bg-secondary hover:bg-border",
            )}
          >
            <p className="text-[11px] text-muted-foreground truncate">{s.label}</p>
            <p className={cn("font-serif text-xl", s.key === "WON" ? "text-success" : "text-foreground")}>{counts[s.key]}</p>
          </Link>
        ))}
      </div>

      {open.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No open enquiries right now.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-1 gap-3">
          {open.map((e) => (
            <Link
              key={e._id}
              to="/enquiries"
              data-testid={`pipeline-card-${e._id}`}
              className="group rounded-xl border border-border overflow-hidden hover:shadow-md transition-shadow flex sm:block xl:flex"
            >
              <div className="relative w-24 sm:w-auto xl:w-24 shrink-0">
                <img src={roomImageForSeed(e._id)} alt="" className="h-full sm:h-20 xl:h-full w-full object-cover" loading="lazy" />
                <span className={cn("absolute bottom-1.5 left-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium", TAG[e.status].className)}>
                  {TAG[e.status].label}
                </span>
              </div>
              <div className="p-2.5 min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">{e.customerName || "Customer"}</p>
                <p className="text-[11px] text-muted-foreground">{timeAgo(e.updatedAt)}</p>
                <div className="flex items-center justify-between mt-1.5 gap-1">
                  <p className="text-xs text-accent font-medium truncate">
                    {e.potentialValue ? `${formatINR(e.potentialValue)} potential` : "Value TBD"}
                  </p>
                  <span className="h-6 w-6 rounded-full border border-border flex items-center justify-center shrink-0 group-hover:border-primary group-hover:text-primary transition-colors">
                    <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
